import { t } from "i18next";
import { ComponentProps } from "react";
import { ComputeFieldComponent } from "../computeFields";
import { MultipleprintInBibliography } from "../types";
import { resolveBibliographieResumeLong } from "./resolveBibliographieResumeLong";
import { renderReproductionPart } from "./shared/bibliography-reproduction";
import { SeparatedList } from "./shared/utils";

export const BibliographyDescriptionMultipleprintCF: ComputeFieldComponent<
  Pick<
    MultipleprintInBibliography,
    | "bibliography"
    | "bibliographieDansEstampesMultiplesDescriptionCitePage"
    | "bibliographieDansEstampesMultiplesDescriptionReproduitPlanche"
    | "biblioInMultiPrintDescPlancheNonNum"
    | "bibliographieDansEstampesMultiplesDescriptionListePage"
    | "bibliographieDansEstampesMultiplesDescriptionSousNumero"
    | "bibliographieDansEstampesMultiplesDescriptionReproductionCoul"
    | "biblioInMultiPrintDescReproCover"
    | "bibliographieDansEstampesMultiplesDescriptionNonPagine"
    | "bibliographieDansEstampesMultiplesDescriptionTexteLibre"
    | "bibliographieDansEstampesMultiplesDescriptionPhotographe"
    | "bibliographieDansEstampesMultiplesDescriptionReproduitPage"
    | "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr"
  >
> = ({ input, language, ...props }) => {
  const resumeBiblio = resolveBibliographieResumeLong(
    input.bibliography,
    language,
  );
  const texteLibre = (
    input.bibliographieDansEstampesMultiplesDescriptionTexteLibre ?? ""
  ).trim();

  if (texteLibre) {
    return (
      <SeparatedList
        {...props}
        lineBreak={false}
        language={language}
        commaSeparated
        input={[
          <span dangerouslySetInnerHTML={{ __html: resumeBiblio }} />,
          texteLibre,
        ]}
      />
    );
  }

  const coul =
    !!input.bibliographieDansEstampesMultiplesDescriptionReproductionCoul;
  const couv = !!input.biblioInMultiPrintDescReproCover;
  const nP = !!input.bibliographieDansEstampesMultiplesDescriptionNonPagine;
  const page = (
    input.bibliographieDansEstampesMultiplesDescriptionReproduitPage ?? ""
  ).trim();
  const planche = (
    input.bibliographieDansEstampesMultiplesDescriptionReproduitPlanche ?? ""
  ).trim();
  const plancheNonNum = !!input.biblioInMultiPrintDescPlancheNonNum;
  const citePage = (
    input.bibliographieDansEstampesMultiplesDescriptionCitePage ?? ""
  ).trim();
  const listePage = (
    input.bibliographieDansEstampesMultiplesDescriptionListePage ?? ""
  ).trim();
  const sousNumero = (
    input.bibliographieDansEstampesMultiplesDescriptionSousNumero ?? ""
  ).trim();

  const reproducePart = renderReproductionPart(
    { coul, couv, nP, page, planche, plancheNonNum },
    t,
  );

  const citePart = citePage && (
    <>
      {t("computeField.citePageAbbrege")} {citePage}
    </>
  );

  const listePart = listePage && (
    <>
      {t("computeField.listePageAbbrege")} {listePage}
      {sousNumero && (
        <>
          {" "}
          ({t("computeField.sousNumeroPrefix")}
          {sousNumero})
        </>
      )}
    </>
  );

  return (
    <SeparatedList
      {...props}
      lineBreak={false}
      language={language}
      commaSeparated
      input={[
        <span dangerouslySetInnerHTML={{ __html: resumeBiblio }} />,
        citePart,
        reproducePart,
        listePart,
      ]}
    />
  );
};

export const RelatedBibliographiesMultipleprintsCF: ComputeFieldComponent<{
  relatedBibliographies?: ComponentProps<
    typeof BibliographyDescriptionMultipleprintCF
  >["input"][];
}> = ({ input, lineBreak = true, ...props }) => {
  if (
    !input.relatedBibliographies ||
    input.relatedBibliographies.length === 0
  ) {
    return <></>;
  }
  return (
    <SeparatedList
      {...props}
      input={input.relatedBibliographies.map((bibliography) => (
        <BibliographyDescriptionMultipleprintCF
          input={bibliography}
          {...props}
        />
      ))}
      lineBreak={lineBreak}
    />
  );
};
